import { DateMatrixTable } from '@/components/dateMatrix/DateMatrixTable';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { MonthPicker } from '@/components/shared/date/MonthPicker';
import { Loader } from '@/components/shared/Loader';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat, getDatePeriod, getMonthBounds, isDateInPast } from '@/helpers/dateHelper';
import { useMapToLeaveTypeId } from '@/services/availability/useAvailabilityTypes';
import { buildSchedule } from '@/services/employee/schedule/buildSchedule';
import { nonSuspenseQueryContext, useRetainedQueryState } from '@/services/gql/config/useRetainedQueryState';
import {
  EmployeeAvailabilityExceptions,
  EmployeePattern,
  useGetEmployeesByLocationsQuery,
  useGetRosteredStaffInDateRangeQuery,
  useLocationExceptionsQuery,
  useLocationWorkingPatternsQuery
} from '@/services/gql/graphql.generated';
import { DayOfWeekAndHoliday } from '@/services/holiday/holiday.types';
import { useGetDayOfWeekAndHoliday } from '@/services/holiday/usePublicHolidays';
import { useLocationService } from '@/services/location/LocationService';
import { displayName } from '@/services/translation/name';
import { FC, useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { FiCalendar } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import strings from '../team.translations';
import { TeamCalendarCell } from './TeamCalendarCell';
import { TeamCalendarLeaveTypeKey } from './TeamCalendarLeaveTypeKey';
import { TeamCalendarRowHeader } from './TeamCalendarRowHeader';

const nameColumnWidth = 280;
const dayColumnMinWidth = 40;
const dayColumnMaxWidth = 53;

type TeamCalendarScheduleByDate = ReturnType<ReturnType<typeof buildSchedule>>;

const parseDateParam = (date: string) => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const buildEmptyScheduleForMonth = (dateRange: Date[]): TeamCalendarScheduleByDate => {
  return dateRange.reduce<TeamCalendarScheduleByDate>((schedule, date) => {
    schedule[dateToApiFormat(date)] = {
      date,
      availableTimes: [],
      shifts: []
    };

    return schedule;
  }, {});
};

const hasCompletePatterns = (
  patterns: NonNullable<EmployeePattern['periods']>[number]['patterns'] = [],
  dateRange: Date[],
  getDayOfWeekAndHoliday: (date: Date) => DayOfWeekAndHoliday
) => {
  return dateRange.every(date => {
    const dayOfWeekAndHoliday = getDayOfWeekAndHoliday(date);
    return patterns.some(pattern => pattern.dayNumber === dayOfWeekAndHoliday);
  });
};

const useTeamCalendarQueryData = (locationId: number | undefined, startDate: Date, endDate: Date) => {
  // These queries intentionally opt out of Suspense so that month navigation keeps the current table on screen
  // while the next month is loading in the background.
  const [employeeQuery] = useGetEmployeesByLocationsQuery({
    variables: { locationIds: locationId ? [locationId] : [] },
    context: nonSuspenseQueryContext,
    pause: !locationId,
    requestPolicy: 'cache-and-network'
  });

  const [workingPatternsQuery] = useLocationWorkingPatternsQuery({
    variables: { locationId: locationId || 0, date: dateToApiFormat(startDate), endDate: dateToApiFormat(endDate) },
    context: nonSuspenseQueryContext,
    pause: !locationId,
    requestPolicy: 'cache-and-network'
  });

  const [exceptionsQuery] = useLocationExceptionsQuery({
    variables: {
      locationId: locationId || 0,
      startDate: dateToApiFormat(startDate),
      endDate: dateToApiFormat(endDate)
    },
    context: nonSuspenseQueryContext,
    pause: !locationId,
    requestPolicy: 'cache-and-network'
  });

  const [rostersQuery] = useGetRosteredStaffInDateRangeQuery({
    variables: {
      locationId: locationId || 0,
      startDate: dateToApiFormat(startDate),
      endDate: dateToApiFormat(endDate)
    },
    context: nonSuspenseQueryContext,
    pause: !locationId,
    requestPolicy: 'cache-and-network'
  });

  const employeeState = useRetainedQueryState(employeeQuery, { pause: !locationId, resetKey: locationId });
  const patternState = useRetainedQueryState(workingPatternsQuery, { pause: !locationId, resetKey: locationId });
  const exceptionState = useRetainedQueryState(exceptionsQuery, { pause: !locationId, resetKey: locationId });
  const rosterState = useRetainedQueryState(rostersQuery, { pause: !locationId, resetKey: locationId });

  return {
    errors: [employeeQuery.error, workingPatternsQuery.error, exceptionsQuery.error, rostersQuery.error],
    employees: employeeState.resolvedData?.employeesByLocations?.employees ?? [],
    workingPatterns: patternState.resolvedData?.locationWorkingPatterns?.employeePatterns ?? [],
    employeeAvailabilityExceptions:
      exceptionState.resolvedData?.locationAvailabilityExceptions?.employeeAvailabilityExceptions ?? [],
    rosteredStaff: rosterState.resolvedData?.rosteredStaffInDateRange ?? [],
    isInitialLoading:
      employeeState.isInitialLoading ||
      patternState.isInitialLoading ||
      exceptionState.isInitialLoading ||
      rosterState.isInitialLoading,
    isRefreshing:
      employeeState.isRefreshing || patternState.isRefreshing || exceptionState.isRefreshing || rosterState.isRefreshing
  };
};

export const TeamCalendarPage: FC = () => {
  const handleError = useErrorHandler();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedLocation = useLocationService(state => state.selectedLocation);
  const anchorDateParam = searchParams.get('anchorDate');
  const [selectedDate, setSelectedDate] = useState<Date>(
    anchorDateParam ? parseDateParam(anchorDateParam) : new Date()
  );

  const selectedRange = useMemo(() => {
    return getMonthBounds(selectedDate);
  }, [selectedDate]);

  const [startDate, endDate] = selectedRange;
  const dateRange = useMemo(() => getDatePeriod(startDate, endDate), [startDate, endDate]);
  const mapToLeaveTypeId = useMapToLeaveTypeId();
  const getDayOfWeekAndHoliday = useGetDayOfWeekAndHoliday(startDate);

  const locationId = selectedLocation?.id;
  const setSelectedDateAndPersist = (date: Date) => {
    setSelectedDate(date);

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('anchorDate', dateToApiFormat(date));
    setSearchParams(nextSearchParams, { replace: true });
  };

  const {
    errors,
    employees: employeeRecords,
    workingPatterns,
    employeeAvailabilityExceptions,
    rosteredStaff,
    isInitialLoading,
    isRefreshing
  } = useTeamCalendarQueryData(locationId, startDate, endDate);

  errors.forEach(error => {
    if (error) {
      handleError(error);
    }
  });

  const employees = useMemo(() => {
    return (
      employeeRecords
        ?.filter(employee => employee?.isRosterable)
        .filter(
          employee => !employee.leaveDate || dateToApiFormat(startDate) <= dateToApiFormat(new Date(employee.leaveDate))
        )
        .sort((a, b) => displayName(a).localeCompare(displayName(b))) ?? []
    );
  }, [employeeRecords, startDate]);

  const rows = useMemo(() => {
    const buildScheduleForMonth = buildSchedule(dateRange, mapToLeaveTypeId, getDayOfWeekAndHoliday);
    const emptyScheduleForMonth = buildEmptyScheduleForMonth(dateRange);

    return employees.map(employee => {
      const employeePattern = workingPatterns.find(pattern => pattern.identityId === employee.identityId) as
        | EmployeePattern
        | undefined;
      const employeeExceptions = employeeAvailabilityExceptions.find(
        availability => availability.employeeId === employee.identityId
      ) as EmployeeAvailabilityExceptions | undefined;

      const patterns = employeePattern?.periods?.[0]?.patterns ?? [];
      const exceptions = employeeExceptions?.patternsAndExceptions?.[0]?.exceptions ?? [];
      const shifts = rosteredStaff.filter(roster => roster.identityId === employee.identityId);

      // Query responses can arrive at slightly different times during month navigation.
      // If pattern data for an employee is still incomplete, render an empty row until the
      // matching pattern payload is complete rather than throwing inside buildSchedule.
      const scheduleByDate = hasCompletePatterns(patterns, dateRange, getDayOfWeekAndHoliday)
        ? buildScheduleForMonth(patterns, exceptions, shifts)
        : emptyScheduleForMonth;

      return {
        employee,
        scheduleByDate
      };
    });
  }, [
    dateRange,
    employeeAvailabilityExceptions,
    employees,
    getDayOfWeekAndHoliday,
    mapToLeaveTypeId,
    rosteredStaff,
    workingPatterns
  ]);

  const renderDateHeader = (date: Date) => {
    const dayType = getDayOfWeekAndHoliday(date);
    const textClass =
      dayType === DayOfWeekAndHoliday.SATURDAY ||
      dayType === DayOfWeekAndHoliday.SUNDAY ||
      dayType === DayOfWeekAndHoliday.HOLIDAY
        ? 'text-green-500'
        : 'text-secondary-std';

    return (
      <div className={`border-b border-r border-primary-std px-2 py-2 text-center text-xs font-semibold ${textClass}`}>
        <div>{date.toLocaleDateString('en-GB', { weekday: 'narrow', timeZone: 'UTC' })}</div>
        <div className="text-sm">{date.getUTCDate()}</div>
      </div>
    );
  };

  if (!selectedLocation) {
    return (
      <PageContainer>
        <PageHeader
          pageTitle={strings.teamCalendarPageTitle}
          icon={<FiCalendar />}
          breadcrumbs={[{ name: strings.teamCalendarPageTitle, to: '/team/calendar' }]}
        />
        <PageSizer mode={SizerMode.PAGE}>{strings.teamCalendar.noLocation}</PageSizer>
      </PageContainer>
    );
  }

  if (isInitialLoading) {
    return (
      <PageContainer fullWidth>
        <PageHeader
          pageTitle={strings.teamCalendarPageTitle}
          icon={<FiCalendar />}
          breadcrumbs={[{ name: strings.teamCalendarPageTitle, to: '/team/calendar' }]}
        />
        <PageSizer mode={SizerMode.PAGE}>
          <TableLoader />
        </PageSizer>
      </PageContainer>
    );
  }

  return (
    <PageContainer fullWidth>
      <PageHeader
        pageTitle={strings.teamCalendarPageTitle}
        icon={<FiCalendar />}
        breadcrumbs={[{ name: strings.teamCalendarPageTitle, to: '/team/calendar' }]}
        fullWidth
      />
      <PageSizer className="space-y-5 bg-white border-b" mode={SizerMode.FULL}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <MonthPicker value={startDate} onChange={setSelectedDateAndPersist} showLabels={false} position="left" />
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            {isRefreshing && (
              <div className="flex items-center gap-2 text-sm text-secondary-std">
                <Loader className="h-5 w-5" />
                <span>{strings.teamCalendar.refreshing}</span>
              </div>
            )}
          </div>
        </div>
      </PageSizer>
      <DateMatrixTable
        dates={dateRange}
        rows={rows}
        rowKey={row => row.employee.identityId!}
        leftHeader={null}
        rowHeightClassName="h-[53px]"
        leftColumnWidth={nameColumnWidth}
        dayColumnMinWidth={dayColumnMinWidth}
        dayColumnMaxWidth={dayColumnMaxWidth}
        renderDateHeader={renderDateHeader}
        renderRowHeader={row => (
          <TeamCalendarRowHeader employee={row.employee} teamCalendarAnchorDate={dateToApiFormat(startDate)} />
        )}
        renderCell={(row, date) => {
          const key = dateToApiFormat(date);
          const cell = row.scheduleByDate[key];

          return (
            <div
              className={`h-full ${
                isDateInPast(parseDateParam(key), new Date()) ? 'bg-primary-std' : 'bg-primary-std'
              }`}
              style={isDateInPast(parseDateParam(key), new Date()) ? { backgroundColor: '#f5f5f2' } : undefined}
            >
              <TeamCalendarCell
                date={date}
                employeeId={row.employee.identityId!}
                cell={cell}
                teamCalendarAnchorDate={dateToApiFormat(startDate)}
              />
            </div>
          );
        }}
      />
      <TeamCalendarLeaveTypeKey />
    </PageContainer>
  );
};
